import { useState } from "react";
import { CheckCircle2, X, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

export default function MockRazorpayModal({
  open,
  orderId,
  amount,
  currency = "INR",
  keyId,
  doctor,
  onSuccess,
  onFailure,
}) {
  const [processing, setProcessing] = useState(false);

  const handleSuccess = () => {
    setProcessing(true);
    // simulate a network roundtrip
    setTimeout(() => {
      const payId = "pay_" + Math.random().toString(36).slice(2, 18);
      setProcessing(false);
      onSuccess(payId);
    }, 700);
  };

  const handleFailure = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      onFailure();
    }, 500);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v && !processing ? onFailure() : null)}>
      <DialogContent
        data-testid="mock-razorpay-modal"
        className="p-0 overflow-hidden max-w-[420px] rounded-2xl border-0 shadow-2xl [&>button[type=button]:last-of-type]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="sr-only">
          <DialogTitle>Razorpay test payment for ₹{amount}</DialogTitle>
          <DialogDescription>Mocked payment gateway for Healthink booking.</DialogDescription>
        </div>
        {/* Header */}
        <div className="bg-[#0a1a3b] text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-base tracking-tight">Razorpay</span>
            <span className="text-[10px] font-bold uppercase tracking-widest bg-yellow-400 text-[#0a1a3b] px-1.5 py-0.5 rounded">
              Test Mode
            </span>
          </div>
          <button
            type="button"
            onClick={handleFailure}
            data-testid="razorpay-close-btn"
            disabled={processing}
            className="text-white/70 hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 bg-white">
          <div className="text-center">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Healthink — Appointment
            </div>
            <div
              data-testid="razorpay-amount"
              className="text-4xl font-bold tracking-tight mt-1"
            >
              ₹{amount}
              <span className="text-xs text-muted-foreground ml-1">{currency}</span>
            </div>
            {doctor && (
              <div className="text-sm text-muted-foreground mt-1">
                Consult with {doctor.name}
              </div>
            )}
          </div>

          <div className="mt-6 bg-secondary/60 rounded-xl p-3 text-xs text-muted-foreground space-y-1 font-mono">
            <div className="flex justify-between gap-2">
              <span>Order</span>
              <span className="truncate">{orderId}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span>Key</span>
              <span>{keyId}</span>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <Button
              data-testid="razorpay-simulate-success-btn"
              onClick={handleSuccess}
              disabled={processing}
              className="w-full h-12 rounded-xl bg-[#0a1a3b] hover:bg-[#0a1a3b]/90 text-white hover:text-white font-semibold"
            >
              {processing ? "Processing…" : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Simulate Success — Pay ₹{amount}
                </>
              )}
            </Button>
            <Button
              data-testid="razorpay-simulate-failure-btn"
              onClick={handleFailure}
              disabled={processing}
              variant="outline"
              className="w-full h-12 rounded-xl"
            >
              Simulate Failure
            </Button>
          </div>

          <div className="mt-5 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" /> No real charge — this is a mocked gateway.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
